/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState, useEffect } from "react";
import "./client-selection.scss";

interface Client {
  id: string;
  name: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  lastVisit: {
    date: string;
    services: string[];
    stylist: string;
    totalCost: number;
    notes?: string;
  };
}

interface ClientSelectionProps {
  onClientSelect: (client: Client) => void;
}

export function ClientSelection({ onClientSelect }: ClientSelectionProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  useEffect(() => {
    // Load clients data
    fetch('/data/clients.json')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setClients(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading clients:', error);
        setLoading(false);
      });
  }, []);

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
  };

  const handleStartConversation = () => {
    if (selectedClient) {
      onClientSelect(selectedClient);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="client-selection-container">
        <div className="loading">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="client-selection-container">
      <div className="client-selection-content">
        <div className="header">
          <div className="salon-info">
            <div className="salon-logo">
              <span className="logo-icon">✂️</span>
            </div>
            <div className="salon-details">
              <h1>Wacky Hairdo Salon</h1>
              <p>Professional Hair Styling & Beauty Services</p>
            </div>
          </div>
        </div>

        <div className="selection-section">
          <h2>Select a Client to Start Conversation</h2>
          <p className="subtitle">Choose a client from the list below to begin scheduling or consultation</p>

          <div className="client-grid">
            {clients.map((client) => (
              <div
                key={client.id}
                className={`client-card ${selectedClient?.id === client.id ? 'selected' : ''}`}
                onClick={() => handleClientSelect(client)}
              >
                <div className="client-avatar">
                  <span className="avatar-text">
                    {client.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="client-info">
                  <h3 className="client-name">{client.name}</h3>
                  <p className="client-contact">{client.phone}</p>
                  <p className="client-email">{client.email}</p>
                  <div className="last-visit">
                    <span className="visit-label">Last Visit:</span>
                    <span className="visit-date">{formatDate(client.lastVisit.date)}</span>
                  </div>
                  <div className="last-services">
                    {client.lastVisit.services.join(', ')}
                  </div>
                </div>
                <div className="selection-indicator">
                  {selectedClient?.id === client.id && (
                    <div className="checkmark">✓</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selectedClient && (
            <div className="action-section">
              <div className="selected-client-info">
                <h3>Selected Client: {selectedClient.name}</h3>
                <p>Ready to start conversation and assist with scheduling</p>
              </div>
              <button 
                className="start-conversation-btn"
                onClick={handleStartConversation}
              >
                Start Conversation with {selectedClient.name}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 